import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useProfile, useRemoveProfileImage } from '../hooks/useProfile';
import { useAllEvents } from '../hooks/useEvents';
import { useClubs } from '../hooks/useClubs';
import { useProfileImageUpload } from '../hooks/useProfileImageUpload';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { TopBar, ActivityCard, UserBadge, ProfileHeaderSkeleton, ActivityCardSkeleton, Skeleton } from '../components';
import AvatarCropModal from '../components/AvatarCropModal';
import { AvatarRemoveButton } from '../components/AvatarRemoveButton';
import { Camera, Settings, Shield, LogOut, Trophy, MapPin, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { countLabel } from '../lib/formatters';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { data: profile, isLoading: profileLoading, error: profileError } = useProfile();

  // Set page title for browser history (#896)
  useDocumentTitle('Profile');
  const { data: events, isLoading: eventsLoading, error: eventsError } = useAllEvents();
  const { data: clubs, isLoading: clubsLoading, error: clubsError } = useClubs();
  const { uploadImage, isUploading, error: uploadError, reset: resetUpload } = useProfileImageUpload();
  const { mutate: removeProfileImage, isPending: isRemoving } = useRemoveProfileImage();
  // Stats tab hidden for App Store compliance (#3277) - "coming soon" placeholder
  // scrubbed from the shipped app; re-add 'stats' when real analytics exist.
  const [activeTab, setActiveTab] = useState<'activity' | 'clubs'>('activity');
  const [showCropModal, setShowCropModal] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    const maxSize = 20 * 1024 * 1024; // 20MB - CF Image Resizing handles optimization (#792)
    if (file.size > maxSize) {
      toast.error('Image must be under 20MB');
      return;
    }

    // Show crop modal for user to crop the image
    setPendingFile(file);
    setShowCropModal(true);
  };

  const handleCropComplete = async (croppedFile: File) => {
    setShowCropModal(false);
    setPendingFile(null);
    
    try {
      await uploadImage(croppedFile);
      // Success - the hook will automatically refresh the profile data
    } catch {
      // Error is handled by the hook and displayed in UI
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setPendingFile(null);
  };

  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  // Transform events to recent activity format
  const recentActivities = (() => {
    if (!events || events.length === 0) return [];
    
    const now = new Date();
    // #2247: bucket by LOCAL CALENDAR DAY, not elapsed-ms arithmetic (the
    // formatCompactDate #2259 pattern) — now−24h mislabels in the hour after
    // spring-forward, and Math.round absorbs 23h/25h DST-transition days.
    const startOfDay = (d: Date) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const dayDiff = (d: Date) =>
      Math.round((startOfDay(d) - startOfDay(now)) / (1000 * 60 * 60 * 24));

    const recentEvents = events
      .filter(event => {
        const eventDate = new Date(event.starts_at * 1000);
        // Already happened, within the last 30 calendar days
        return eventDate <= now && dayDiff(eventDate) >= -30;
      })
      .sort((a, b) => b.starts_at - a.starts_at) // Most recent first
      .slice(0, 10); // Limit to 10 activities

    return recentEvents.map(event => {
      const eventDate = new Date(event.starts_at * 1000);
      const diff = dayDiff(eventDate);

      let timeDisplay: string;
      if (diff === 0) {
        timeDisplay = 'Today, ' + eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      } else if (diff === -1) {
        timeDisplay = 'Yesterday, ' + eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      } else {
        timeDisplay = eventDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        }) + ', ' + eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      }
      
      return {
        id: event.id,
        short_code: event.short_code,
        title: event.title || event.name || 'Activity',
        time: timeDisplay,
        location: event.location_name || 'Location not specified',
        participants: event.rsvp_count || 0,
        // #1962: prefer the card-sized variant (480x240) over the full hero (1200x675)
        // for the EventCard render slot. Backend COALESCE handles event → club fallback
        // before the card variant is derived; falls back to the raw hero if the
        // backend hasn't enriched yet (older event rows or non-enriched endpoints).
        imageUrl: event.card_image_url || event.hero_image_url || '',
        event_number: event.event_number ?? null
      };
    });
  })();

  const isLoading = profileLoading || eventsLoading || clubsLoading;
  // When clubs fail, useAllEvents resolves [] rather than erroring (its fan-out
  // has no club list) — the events surfaces must not claim "0" in that case.
  const eventsUnavailable = !!eventsError || !!clubsError;

  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar title="Profile" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-6 space-y-6">
            <ProfileHeaderSkeleton />

            {/* Activity section skeleton */}
            <div className="space-y-4">
              <Skeleton className="h-6 w-32" />
              {[1, 2, 3].map((i) => (
                <ActivityCardSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (profileError) {
    // Transport failure ≠ data absence — don't tell an offline user their
    // profile doesn't exist (matches the native /me error branch, #2213).
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar title="Profile" onBack={() => navigate(-1)} />
        <div className="flex-1 flex items-center justify-center pt-14">
          <div className="text-oro-error">Couldn&apos;t load your profile.</div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar title="Profile" onBack={() => navigate(-1)} />
        <div className="flex-1 flex items-center justify-center pt-14">
          <div className="text-text-muted">Profile not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg">
      {/* TopBar */}
      <TopBar
        title="Profile"
        onBack={() => navigate(-1)}
      />

      {/* Upload Error Display */}
      {uploadError && (
        <div className="px-4 pt-4">
          <div className="bg-oro-error/10 border border-oro-error/20 rounded-lg p-3 flex items-start gap-2">
            <div className="text-oro-error text-sm font-medium flex-1">
              {uploadError}
            </div>
            <button
              onClick={resetUpload}
              className="text-oro-error/70 hover:text-oro-error"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto pt-14">
        {/* Hero Section - Avatar + Cover */}
        <div className="relative">
          {/* Cover Photo */}
          <div className="h-32 bg-linear-to-b from-oro-surface/20 to-transparent relative overflow-hidden">
            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 opacity-5">
              <svg width="100%" height="100%" viewBox="0 0 400 200">
                <path d="M0,50 Q100,40 200,50 T400,50" fill="none" stroke="currentColor" strokeWidth="1"/>
                <path d="M0,80 Q150,70 300,80 T400,80" fill="none" stroke="currentColor" strokeWidth="1"/>
                <path d="M0,110 Q100,120 200,110 T400,110" fill="none" stroke="currentColor" strokeWidth="1"/>
              </svg>
            </div>
          </div>

          {/* Avatar Card - Overlapping Cover */}
          <div className="px-4 -mt-12 relative z-20" data-tour="profile-header">
            <div className="bg-oro-surface rounded-2xl shadow-card border border-oro-border p-5">
              <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className="relative z-30">
                  <div className="w-20 h-20 rounded-full bg-oro-surface-alt flex items-center justify-center text-3xl font-bold text-text-primary ring-2 ring-oro-border overflow-hidden">
                    {profile.profile_image_url ? (
                      <img 
                        src={profile.profile_image_url} 
                        alt={profile.public_display_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      profile.public_display_name.charAt(0).toUpperCase()
                    )}
                  </div>
                  <button 
                    onClick={handleCameraClick}
                    disabled={isUploading}
                    className={`absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-oro-accent shadow-lg flex items-center justify-center transition-all z-40 ${
                      isUploading 
                        ? 'opacity-50 cursor-not-allowed' 
                        : 'hover:bg-oro-accent/90 hover:scale-110'
                    }`}
                  >
                    <Camera className={`w-4 h-4 text-white ${isUploading ? 'animate-pulse' : ''}`} />
                  </button>
                  
                  {/* Remove avatar button - only show when avatar exists */}
                  {profile?.profile_image_url && (
                    <AvatarRemoveButton
                      onRemove={() => removeProfileImage()}
                      isLoading={isRemoving}
                    />
                  )}
                  
                  {/* Hidden file input with full picker options */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>                {/* Name + Badge */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h1 className="text-xl font-bold text-text-primary truncate">
                      {profile.public_display_name}
                    </h1>
                    {/* subscription_level was never part of the /profile
                        envelope (see lib/api/profile.ts) — the cast read
                        `undefined` at runtime. UserBadge is DORMANT (renders null) — private, self-facing badges, see #3356. */}
                    <UserBadge size="sm" />
                  </div>
                  {profile.tagline && (
                    <p className="text-sm text-text-secondary line-clamp-2">
                      {profile.tagline}
                    </p>
                  )}
                  {profile.location && (
                    <p className="text-xs text-text-muted mt-1">
                      <MapPin className="w-3 h-3 inline mr-1" />{profile.location}
                    </p>
                  )}
                </div>

                {/* Platform Admin Button (if admin) */}
                {user?.is_platform_admin && (
                  <button 
                    onClick={() => navigate('/platform-admin/home')}
                    className="w-9 h-9 rounded-lg bg-accent/10 hover:bg-accent/20 border border-accent/30 flex items-center justify-center transition-colors"
                    title="Platform Admin Dashboard"
                  >
                    <Shield className="w-4 h-4 text-accent" />
                  </button>
                )}

                {/* Settings Button */}
                <button 
                  onClick={() => navigate('/settings/profile')}
                  className="w-9 h-9 rounded-lg bg-oro-surface-alt hover:bg-oro-surface border border-oro-border flex items-center justify-center transition-colors"
                >
                  <Settings className="w-4 h-4 text-text-primary" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="px-4 mt-6">
          <div className="flex items-center justify-around py-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-text-primary">{eventsUnavailable ? '—' : events?.length || 0}</div>
              <div className="text-xs text-text-muted uppercase tracking-wide mt-1">Events</div>
            </div>
            <div className="w-px h-10 bg-oro-border"></div>
            <div className="text-center">
              <div className="text-2xl font-bold text-text-primary">{clubsError ? '—' : clubs?.length || 0}</div>
              <div className="text-xs text-text-muted uppercase tracking-wide mt-1">Clubs</div>
            </div>
            <div className="w-px h-10 bg-oro-border"></div>
            <div className="text-center">
              <div className="text-2xl font-bold text-text-primary">0</div>
              <div className="text-xs text-text-muted uppercase tracking-wide mt-1">Achievements</div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="px-4 mt-4">
          <div className="flex items-center gap-1 bg-oro-surface-alt rounded-xl p-1 border border-oro-border">
            <button
              onClick={() => setActiveTab('activity')}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'activity'
                  ? 'bg-oro-surface text-text-primary shadow-sm'
                  : 'text-text-muted hover:text-text-secondary'
              }`}
            >
              Activity
            </button>
            <button
              onClick={() => setActiveTab('clubs')}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'clubs'
                  ? 'bg-oro-surface text-text-primary shadow-sm'
                  : 'text-text-muted hover:text-text-secondary'
              }`}
            >
              Clubs
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-4 mt-6">
          {activeTab === 'activity' && (
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">
                Recent Activity
              </h2>
              {eventsUnavailable ? (
                <p className="text-oro-error text-center py-12">Couldn&apos;t load your events.</p>
              ) : recentActivities.length > 0 ? (
                recentActivities.map(activity => (
                  <ActivityCard
                    key={activity.id}
                    activity={activity}
                    onClick={() => navigate(`/event/${activity.short_code || activity.id}`)}
                  />
                ))
              ) : (
                <div className="text-center py-12 text-text-muted">
                  <Trophy className="w-12 h-12 text-text-muted mx-auto mb-3" />
                  <p>No recent activity</p>
                  <p className="text-xs mt-2">Join some events to see your activity here</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'clubs' && (
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">
                Your Clubs
              </h2>
              {clubsError ? (
                <p className="text-oro-error text-center py-12">Couldn&apos;t load your clubs.</p>
              ) : clubs && clubs.length > 0 ? (
                clubs.map(club => (
                  <div 
                    key={club.id} 
                    className="bg-oro-surface border border-oro-border rounded-xl p-4 hover:border-oro-gold transition-colors cursor-pointer"
                    onClick={() => navigate(`/clubs/${club.slug}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-oro-gold/20 flex items-center justify-center overflow-hidden">
                        {/* #562: avatar_image_url is the dedicated club-avatar field;
                            GET /clubs never returns cover_image_url (dead term, #2247) */}
                        {club.avatar_image_url || club.hero_image_url ? (
                          <img
                            src={(club.avatar_image_url || club.hero_image_url) || undefined}
                            alt={club.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            width={48}
                            height={48}
                          />
                        ) : (
                          <span className="text-sm font-bold text-accent">{club.name.charAt(0)}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-text-primary truncate">{club.name}</h3>
                        <p className="text-xs text-text-muted truncate">
                          {club.location || 'Location not set'} • {countLabel(club.member_count || 0, 'member')}
                        </p>
                      </div>
                      <div className="text-xs text-text-muted">
                        {club.role || 'Member'}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-text-muted">
                  <Users className="w-12 h-12 text-text-muted mx-auto mb-3" />
                  <p>No clubs yet</p>
                  <p className="text-xs mt-2">Join or create a club to get started</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sign Out - Red text, standalone, generous margin (matches SettingsPage) */}
        <section className="px-4 pt-8 pb-24">
          <button
            onClick={logout}
            className="w-full py-3 text-center text-oro-error hover:text-oro-error/80 transition-colors"
          >
            <span className="flex items-center justify-center gap-2">
              <LogOut className="w-4 h-4" />
              Sign Out
            </span>
          </button>
        </section>
      </div>

      {/* Avatar Crop Modal - Compact */}
      {pendingFile && (
        <AvatarCropModal
          isOpen={showCropModal}
          onClose={handleCropCancel}
          imageFile={pendingFile}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  );
}
